// Pinned, browser-native OOXML renderer. It is bundled from the lockfile at
// build time and is never downloaded or executed from a remote CDN.
export const DOCX_RENDERER_PACKAGE = 'docx-preview@0.4.0';

export const DOCX_RENDER_OPTIONS = Object.freeze({
  className: 'shepherd-docx',
  inWrapper: true,
  hideWrapperOnPrint: false,
  ignoreWidth: false,
  ignoreHeight: false,
  ignoreFonts: false,
  breakPages: true,
  // Word writes these markers at its calculated page boundaries. Honouring
  // them is the closest browser-side match to the original pagination.
  ignoreLastRenderedPageBreak: false,
  experimental: true,
  trimXmlDeclaration: true,
  // Embedded images and fonts become local data URLs. No document asset needs
  // a network request and no object URL remains after the preview is closed.
  useBase64URL: true,
  renderHeaders: true,
  renderFooters: true,
  renderFootnotes: true,
  renderEndnotes: true,
  renderChanges: false,
  renderComments: false,
  // altChunk can contain arbitrary HTML and would be rendered in an iframe.
  // It is intentionally disabled for an untrusted meeting upload.
  renderAltChunks: false,
  debug: false,
});

let rendererPromise = null;

export const loadDocxRenderer = () => {
  if (!rendererPromise) {
    rendererPromise = import('docx-preview').catch((error) => {
      rendererPromise = null;
      throw error;
    });
  }
  return rendererPromise;
};

const ACTIVE_CONTENT_SELECTOR = [
  'script',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'audio',
  'video',
  'link',
  'meta',
  'base',
].join(',');

const isSafeRenderedAssetUrl = (value) => {
  const url = String(value || '').trim();
  return (
    !url ||
    url.startsWith('#') ||
    /^data:(?:image\/[a-z0-9.+-]+|font\/[a-z0-9.+-]+|application\/(?:font-[a-z0-9.+-]+|x-font-[a-z0-9.+-]+|vnd\.ms-(?:fontobject|opentype)|vnd\.openxmlformats-officedocument\.obfuscatedfont|octet-stream));base64,/i.test(url)
  );
};

const stripExternalCssUrls = (source) => String(source || '')
  .replace(/@import\s+[^;]+;/gi, '')
  .replace(/url\(\s*(['"]?)(.*?)\1\s*\)/gi, (rule, _quote, url) =>
    isSafeRenderedAssetUrl(url) ? rule : 'url("")'
  );

// docx-preview creates semantic DOM rather than an image. Keep its formatting
// nodes, tables, SVG and MathML, but remove anything that could navigate,
// submit, execute, or fetch an image outside the local DOCX package.
export const hardenRenderedDocx = (container, styleContainer) => {
  if (!container?.querySelectorAll) return;

  container.querySelectorAll(ACTIVE_CONTENT_SELECTOR).forEach((node) => node.remove());
  container.querySelectorAll('*').forEach((node) => {
    Array.from(node.attributes || []).forEach((attribute) => {
      if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
    });
    if (node.hasAttribute('style')) {
      node.setAttribute('style', stripExternalCssUrls(node.getAttribute('style')));
    }
  });
  container.querySelectorAll('a').forEach((anchor) => {
    anchor.removeAttribute('href');
    anchor.removeAttribute('target');
    anchor.removeAttribute('rel');
  });
  container.querySelectorAll('img').forEach((image) => {
    const source = String(image.getAttribute('src') || '').trim();
    if (source && !isSafeRenderedAssetUrl(source)) {
      image.removeAttribute('src');
      if (!image.getAttribute('alt')) image.setAttribute('alt', 'External image omitted');
    }
  });
  container.querySelectorAll('svg, svg *').forEach((node) => {
    ['href', 'xlink:href'].forEach((attribute) => {
      if (node.hasAttribute(attribute) && !isSafeRenderedAssetUrl(node.getAttribute(attribute))) {
        node.removeAttribute(attribute);
      }
    });
  });
  [container, styleContainer].filter(Boolean).forEach((host) => {
    host.querySelectorAll?.('style').forEach((style) => {
      style.textContent = stripExternalCssUrls(style.textContent);
    });
  });
};
